import { useDebounce } from 'ahooks'
import { Avatar, Select } from 'antd'
import { difference, last } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserInfoFragment, useGetViewerQuery, useSearchUserQuery } from '@/generated/graphql'

interface MultipleUserSearchProps {
  value?: { userId: number; avatarUrl: string; name: string }[]
  onChange?: (value: { userId: number; avatarUrl: string; name: string }[]) => void
  defaultUser?: UserInfoFragment
  excludeViewer?: boolean
  singleMode?: boolean
}

export const MultipleUserSearch: React.FC<MultipleUserSearchProps> = ({
  value,
  onChange,
  defaultUser,
  excludeViewer = false,
  singleMode = false,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState('')
  const [users, setUsers] = React.useState<{ userId: number; avatarUrl: string; name: string }[]>(value || [])
  const debouncedSearch = useDebounce(search, { wait: 600 })

  const { data: viewerData } = useGetViewerQuery({
    skip: !excludeViewer,
  })

  const { data, loading: searchLoading } = useSearchUserQuery({
    fetchPolicy: 'no-cache',
    skip: !debouncedSearch,
    variables: {
      filter: {
        search: debouncedSearch,
      },
      pagination: {
        first: 6,
      },
    },
  })

  const usersDisplay = value || users
  const viewerUserId = viewerData?.viewer.userId

  const searchedUsers = data
    ? data.users.edges.map(({ node }) => node).filter((user) => user.userId !== viewerUserId)
    : defaultUser
      ? [defaultUser]
      : usersDisplay

  return (
    <Select
      mode='multiple'
      placeholder={t('search.searchUser')}
      loading={searchLoading}
      style={{ width: '100%', marginBottom: 8 }}
      defaultActiveFirstOption={false}
      filterOption={false}
      value={usersDisplay.map((user) => user.userId)}
      onSearch={(value) => setSearch(value)}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(userIds: number[]) => {
        const storedUserIds = users.map((user) => user.userId)
        const usersToInsert = difference(userIds, storedUserIds)
        const usersToDelete = difference(storedUserIds, userIds)

        const newUsers = searchedUsers.filter((user) => usersToInsert.includes(user.userId))
        const filteredStoredUsers = users.slice().filter((user) => !usersToDelete.includes(user.userId))

        if (singleMode) {
          const user = [...filteredStoredUsers, ...newUsers].filter((user) => user.userId === last(userIds))
          setUsers(user)
          onChange?.(user)
          return
        }

        onChange?.([...filteredStoredUsers, ...newUsers])
        setUsers([...filteredStoredUsers, ...newUsers])
      }}
    >
      {searchedUsers.map((user) => (
        <Select.Option key={user.userId} value={user.userId}>
          <Avatar style={{ marginRight: 4 }} size={16} src={user.avatarUrl} />
          {user.name}
        </Select.Option>
      ))}
    </Select>
  )
}
